/**
 * Evidence Highlighted Text Component
 * Renders text with evidence quotes highlighted using <mark> tags.
 * Supports scroll-to functionality via highlight IDs.
 */

import React, { useEffect, useRef } from 'react';
import { FONT_SIZE_BODY, LINE_HEIGHT_BODY, TEXT_COLOR_PRIMARY, EVIDENCE_HIGHLIGHT_BG, EVIDENCE_HIGHLIGHT_BORDER } from '../cssConstants';

interface EvidenceHighlightedTextProps {
  text: string;
  quotes: string[];
  scrollToQuote?: string | null;  // Quote text to auto-scroll to
  onScrollComplete?: () => void;
}

/**
 * Renders text with evidence quotes highlighted.
 * Uses case-insensitive substring matching to find and wrap quotes in <mark> tags.
 */
const EvidenceHighlightedText: React.FC<EvidenceHighlightedTextProps> = ({
  text,
  quotes,
  scrollToQuote,
  onScrollComplete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to target quote when scrollToQuote changes
  useEffect(() => {
    if (!scrollToQuote || !containerRef.current) return;

    const targetId = `evidence-${hashQuote(scrollToQuote)}`;
    const el = containerRef.current.querySelector(`#${targetId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onScrollComplete?.();
    }
  }, [scrollToQuote, onScrollComplete]);

  if (!text) return null;

  const segments = buildHighlightedSegments(text, quotes);

  return (
    <div
      ref={containerRef}
      style={{
        fontSize: FONT_SIZE_BODY,
        lineHeight: LINE_HEIGHT_BODY,
        color: TEXT_COLOR_PRIMARY,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {segments.map((seg, idx) =>
        seg.highlighted ? (
          <mark
            key={idx}
            id={`evidence-${hashQuote(seg.text)}`}
            style={{
              backgroundColor: EVIDENCE_HIGHLIGHT_BG,
              borderBottom: `2px solid ${EVIDENCE_HIGHLIGHT_BORDER}`,
              padding: '1px 2px',
              borderRadius: '2px',
            }}
          >
            {seg.text}
          </mark>
        ) : (
          <span key={idx}>{seg.text}</span>
        )
      )}
    </div>
  );
};

// ===== Helpers =====

interface TextSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Build an array of text segments with highlighted quotes marked.
 * Uses case-insensitive matching. Handles overlapping quotes by using first match.
 */
function buildHighlightedSegments(text: string, quotes: string[]): TextSegment[] {
  if (!quotes || quotes.length === 0) {
    return [{ text, highlighted: false }];
  }

  // Find all match positions (case-insensitive)
  const textLower = text.toLowerCase();
  const matches: Array<{ start: number; end: number }> = [];

  for (const quote of quotes) {
    if (!quote) continue;
    const quoteLower = quote.toLowerCase().trim();
    let searchFrom = 0;

    while (searchFrom < textLower.length) {
      const idx = textLower.indexOf(quoteLower, searchFrom);
      if (idx === -1) break;
      matches.push({ start: idx, end: idx + quoteLower.length });
      searchFrom = idx + quoteLower.length;
    }
  }

  if (matches.length === 0) {
    return [{ text, highlighted: false }];
  }

  // Sort by start position and merge overlapping
  matches.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [matches[0]];
  for (let i = 1; i < matches.length; i++) {
    const last = merged[merged.length - 1];
    if (matches[i].start <= last.end) {
      last.end = Math.max(last.end, matches[i].end);
    } else {
      merged.push(matches[i]);
    }
  }

  // Build segments
  const segments: TextSegment[] = [];
  let lastEnd = 0;

  for (const match of merged) {
    if (match.start > lastEnd) {
      segments.push({ text: text.slice(lastEnd, match.start), highlighted: false });
    }
    segments.push({ text: text.slice(match.start, match.end), highlighted: true });
    lastEnd = match.end;
  }

  if (lastEnd < text.length) {
    segments.push({ text: text.slice(lastEnd), highlighted: false });
  }

  return segments;
}

/** Simple hash for generating stable DOM IDs from quote text */
function hashQuote(quote: string): string {
  let hash = 0;
  const str = quote.toLowerCase().trim();
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return `q${Math.abs(hash)}`;
}

export default EvidenceHighlightedText;
